/**
 * WORDPRESS BAĞLANTI PANELİ (Uygulama Kolu — Faz 6) — Ayarlar > WordPress sekmesi.
 * İKİ bağlantı yolu:
 *  1) GEOLOP CONNECTOR EKLENTİSİ (önerilen): ZIP indir → WP'ye yükle/etkinleştir →
 *     Ayarlar > Geolop'taki anahtarı buraya yapıştır. Sunucunun Authorization
 *     başlığını yutması sorununu (paylaşımlı hosting klasiği) kökten aşar,
 *     SEO meta yazımı garantilidir.
 *  2) UYGULAMA ŞİFRESİ: eklenti kurulamayan sitelerde yedek yol.
 * Her uygulamadan önce canlı içerik yedeklenir — tek tık geri alma.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useOnay } from "@/components/ui/onay-dialog";
import { Loader2, Plug, Unplug, CheckCircle2, Undo2, ExternalLink, Download, KeyRound, Puzzle, Languages } from "lucide-react";

type Uygulama = { id: number; tur: "revize" | "yeni-icerik" | "geri-alma"; durum: string; createdAt: string; detay?: { wpLink?: string; yeniTitle?: string; eskiTitle?: string } | null };
type WpDurum = { bagli: boolean; baglanti: { siteUrl: string; kullanici: string; siteAdi: string | null; sonKontrol: string | null; tur?: "sifre" | "eklenti"; yazarId?: number | null; yazarAdi?: string | null } | null; uygulamalar: Uygulama[] };
type WpYazar = { id: number; ad: string; kullanici: string; rol: string };

const TUR_TR = { revize: "Revize uygulandı", "yeni-icerik": "İçerik gönderildi", "geri-alma": "Geri alındı" } as const;

export function WpBaglantiPanel() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [yontem, setYontem] = useState<"eklenti" | "sifre">("eklenti");
  const [anahtar, setAnahtar] = useState("");
  const [kullanici, setKullanici] = useState("");
  const [sifre, setSifre] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [indiriliyor, setIndiriliyor] = useState(false);
  const { onayIste, OnayPenceresi } = useOnay();

  const durum = useQuery<WpDurum>({
    queryKey: ["wp-durum", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/wp`)).json(),
  });

  const eklentiIndir = async () => {
    if (!activeDomainId || indiriliyor) return;
    setIndiriliyor(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/wp/eklenti-indir`);
      if (!r.ok) throw new Error("İndirilemedi");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "geolop-connector.zip";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast({ title: "Eklenti indirilemedi", variant: "destructive" }); }
    finally { setIndiriliyor(false); }
  };

  const baglan = useMutation({
    mutationFn: async () => {
      const govde = yontem === "eklenti"
        ? { tur: "eklenti", anahtar, ...(siteUrl.trim() ? { siteUrl: siteUrl.trim() } : {}) }
        : { kullanici, appPassword: sifre, ...(siteUrl.trim() ? { siteUrl: siteUrl.trim() } : {}) };
      const r = await fetch(`/api/domains/${activeDomainId}/wp/baglan`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(govde),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Bağlanamadı");
      return j as { siteAdi?: string };
    },
    onSuccess: (j) => {
      toast({ title: "WordPress bağlandı ✓", description: j.siteAdi ? `Site: ${j.siteAdi}` : undefined });
      setSifre(""); setAnahtar("");
      void qc.invalidateQueries({ queryKey: ["wp-durum", activeDomainId] });
    },
    onError: (e) => toast({ title: "Bağlanamadı", description: String(e instanceof Error ? e.message : e), variant: "destructive" }),
  });

  // GÖNDERİM YAZARI (2026-09-21): içerik WP'de kimin adına çıksın. Eklenti 1.6.0 gerekir;
  // seçilmezse WP ilk yöneticiyi kullanır (tecnifue'da "James Wilson" bu yüzden çıkıyordu).
  const [yazarAcik, setYazarAcik] = useState(false);
  const yazarlar = useQuery<{ yazarlar: WpYazar[]; error?: string }>({
    queryKey: ["wp-yazarlar", activeDomainId],
    enabled: !!activeDomainId && yazarAcik,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/wp/yazarlar`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Yazar listesi alınamadı");
      return j;
    },
  });
  const yazarSec = useMutation({
    mutationFn: async (y: WpYazar | null) => {
      const r = await fetch(`/api/domains/${activeDomainId}/wp/yazar`, {
        method: "PATCH", headers: { "content-type": "application/json" },
        body: JSON.stringify({ yazarId: y?.id ?? null, yazarAdi: y?.ad ?? null }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Kaydedilemedi");
      return j;
    },
    onSuccess: (_d, y) => {
      toast({ title: y ? `Yazar: ${y.ad}` : "Yazar seçimi kaldırıldı", description: y ? "Bundan sonraki gönderimler bu kişinin adına çıkar." : "WordPress varsayılan yöneticisi kullanılacak." });
      setYazarAcik(false);
      void qc.invalidateQueries({ queryKey: ["wp-durum", activeDomainId] });
    },
    onError: (e) => toast({ title: "Yazar kaydedilemedi", description: String(e instanceof Error ? e.message : e), variant: "destructive" }),
  });

  // DİL ONARIMI (2026-10-01): canlı vakada (tecnifue/WPML) eklentinin eski sürümü dili
  // atadığını RAPORLUYOR ama atamıyordu — 75 yazı İngilizce kalmıştı. Bu düğme, Geolop'un
  // gönderdiği tüm yazıları kayıtlardaki doğru dile (brief pazarı / çevirinin hedef dili)
  // tek seferde taşır. Eklenti 1.9.0 gerekir.
  const dilOnar = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/wp/dil-onar`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Onarılamadı");
      return j as { toplam: number; duzeltilen: number; sonuc: { id: number; ok: boolean; dil?: string | null; baslik?: string | null }[] };
    },
    onSuccess: (j) => {
      const kalan = j.toplam - j.duzeltilen;
      toast({
        title: j.toplam === 0 ? "Onarılacak yazı bulunmadı" : `${j.duzeltilen}/${j.toplam} yazının dili düzeltildi`,
        description: j.toplam === 0
          ? "Bu sitede Geolop üzerinden gönderilmiş yazı kaydı yok."
          : kalan > 0 ? `${kalan} yazı düzeltilemedi — WP'de silinmiş olabilir ya da eklenti 1.9.0'dan eski.` : "WordPress'te dil sütunlarını kontrol edebilirsin.",
        variant: kalan > 0 && j.duzeltilen === 0 ? "destructive" : undefined,
      });
    },
    onError: (e) => toast({ title: "Dil onarımı başarısız", description: String(e instanceof Error ? e.message : e), variant: "destructive" }),
  });

  const kopar = useMutation({
    mutationFn: async () => { await fetch(`/api/domains/${activeDomainId}/wp`, { method: "DELETE" }); },
    onSuccess: () => { toast({ title: "Bağlantı koparıldı" }); void qc.invalidateQueries({ queryKey: ["wp-durum", activeDomainId] }); },
  });

  if (durum.isLoading) return <Card className="p-8 text-center text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Okunuyor…</Card>;
  const b = durum.data?.baglanti;

  return (
    <div className="space-y-4">
      {OnayPenceresi}
      {durum.data?.bagli && b ? (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span className="text-[13.5px] font-semibold">{b.siteAdi || b.siteUrl}</span>
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
              {b.tur === "eklenti" ? "EKLENTİ İLE BAĞLI" : "UYGULAMA ŞİFRESİYLE BAĞLI"}
            </span>
            <span className="text-[12px] text-muted-foreground">{b.siteUrl}</span>
            <Button size="sm" variant="outline" className="ml-auto gap-1.5" disabled={indiriliyor}
              title="Eklentinin güncel sürümünü indir — WP'de eskisini kaldırıp bunu yükle, anahtar aynı kalır" onClick={() => void eklentiIndir()}>
              {indiriliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Eklentiyi İndir
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5 text-destructive border-destructive/40"
              disabled={kopar.isPending} onClick={() => { void onayIste({ baslik: "Bağlantı koparılsın mı?", aciklama: "WordPress bağlantısı silinecek; uygulama geçmişi durur, siteye artık uygulama yapılamaz.", onayYazisi: "Kopar", tehlikeli: true }).then((t) => { if (t) kopar.mutate(); }); }}>
              <Unplug className="h-3.5 w-3.5" /> Bağlantıyı Kopar
            </Button>
          </div>
          {/* GÖNDERİM YAZARI — içerik WP'de kimin adına yayınlanacak */}
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed p-2.5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
            <span className="text-[12.5px] font-medium">✍️ Gönderim yazarı:</span>
            <span className="text-[12.5px] text-muted-foreground">{b.yazarAdi ?? "WordPress varsayılanı (ilk yönetici)"}</span>
            {!yazarAcik && (
              <Button size="sm" variant="outline" className="h-7 text-[11.5px]" onClick={() => setYazarAcik(true)}>Değiştir</Button>
            )}
            {yazarAcik && (
              <div className="flex w-full flex-wrap items-center gap-2">
                {yazarlar.isLoading && <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> WordPress kullanıcıları okunuyor…</span>}
                {yazarlar.error && <span className="text-[12px] text-red-600">{String((yazarlar.error as Error).message)}</span>}
                {yazarlar.data?.yazarlar?.map((y) => (
                  <button key={y.id} onClick={() => yazarSec.mutate(y)} disabled={yazarSec.isPending}
                    className={`rounded-full border px-2.5 py-1 text-[11.5px] transition hover:border-brand-400 ${b.yazarId === y.id ? "border-brand-500 bg-brand-50 font-semibold text-brand-600 dark:bg-brand-500/10" : "border-gray-200 dark:border-white/15"}`}>
                    {y.ad} <span className="opacity-60">· {y.rol}</span>
                  </button>
                ))}
                <button onClick={() => yazarSec.mutate(null)} disabled={yazarSec.isPending} className="text-[11.5px] text-muted-foreground hover:underline">varsayılana dön</button>
                <button onClick={() => setYazarAcik(false)} className="text-[11.5px] text-muted-foreground hover:underline">kapat</button>
              </div>
            )}
          </div>
          {/* ÇOK DİLLİ SİTE ONARIMI — yanlış dilde kalmış eski gönderimleri toplu düzeltir */}
          {b.tur === "eklenti" && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-dashed p-2.5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
              <Languages className="h-4 w-4 text-muted-foreground" />
              <span className="text-[12.5px] font-medium">Çok dilli site onarımı:</span>
              <span className="text-[12px] text-muted-foreground">Yanlış dilde (ör. Fransızca yazı İngilizce bölümde) kalmış eski gönderimleri doğru dile taşır.</span>
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-[11.5px]" disabled={dilOnar.isPending}
                onClick={() => { void onayIste({ baslik: "Dilleri onaralım mı?", aciklama: "Geolop'un bu siteye gönderdiği tüm yazılar kayıtlardaki doğru dile taşınacak (çeviriler kaynak yazıya bağlı kalır). WordPress'te eklentinin 1.9.0 sürümü kurulu olmalı.", onayYazisi: "Onar" }).then((t) => { if (t) dilOnar.mutate(); }); }}>
                {dilOnar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />} Dilleri Onar
              </Button>
            </div>
          )}
          <p className="mt-2 text-[12px] text-muted-foreground">
            Sayfa Güçlendirme'deki revizeleri ve İçerik Stüdyosu'ndaki yazıları tek tıkla siteye uygulayabilirsin.
            Her uygulamadan önce canlı içerik yedeklenir — istediğin an geri alırsın.
          </p>
        </Card>
      ) : (
        <>
          {/* Yöntem seçimi */}
          <div className="grid gap-3 md:grid-cols-2">
            <button onClick={() => setYontem("eklenti")}
              className={`rounded-xl border p-4 text-left transition ${yontem === "eklenti" ? "border-brand-500 ring-2 ring-brand-500/25" : "hover:border-gray-300 dark:hover:border-white/20"}`}>
              <div className="flex items-center gap-2">
                <Puzzle className="h-4 w-4" style={{ color: yontem === "eklenti" ? "#e83e3c" : undefined }} />
                <span className="text-[13.5px] font-bold">Geolop eklentisiyle</span>
                <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9.5px] font-bold text-emerald-600 dark:text-emerald-400">ÖNERİLEN</span>
              </div>
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground">
                Küçük bir eklenti kur, anahtarı yapıştır — biter. Hosting'in Authorization başlığını yutması sorununu yaşamaz,
                SEO meta (Rank Math/Yoast) yazımı garantilidir.
              </p>
            </button>
            <button onClick={() => setYontem("sifre")}
              className={`rounded-xl border p-4 text-left transition ${yontem === "sifre" ? "border-brand-500 ring-2 ring-brand-500/25" : "hover:border-gray-300 dark:hover:border-white/20"}`}>
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" style={{ color: yontem === "sifre" ? "#e83e3c" : undefined }} />
                <span className="text-[13.5px] font-bold">Uygulama şifresiyle</span>
              </div>
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground">
                Eklenti kurulamayan siteler için: WP Profil / Uygulama Şifreleri'nden üretilen şifreyle bağlanır.
                Bazı sunucularda ek .htaccess ayarı gerekebilir.
              </p>
            </button>
          </div>

          {yontem === "eklenti" ? (
            <Card className="p-4">
              <div className="mb-2 text-[13px] font-semibold">3 adımda bağla</div>
              <ol className="mb-3 list-decimal space-y-1 pl-5 text-[12.5px] text-muted-foreground">
                <li><button className="font-medium text-brand-500 underline decoration-dotted" disabled={indiriliyor} onClick={() => void eklentiIndir()}>{indiriliyor ? "İndiriliyor…" : "geolop-connector.zip dosyasını indir"}</button> <Download className="inline h-3 w-3" /></li>
                <li>WP yönetim panelinde <b>Eklentiler → Yeni Ekle → Eklenti Yükle</b> ile ZIP'i yükle ve <b>Etkinleştir</b></li>
                <li>WP'de <b>Ayarlar → Geolop</b> sayfasındaki bağlantı anahtarını aşağıya yapıştır</li>
              </ol>
              <div className="grid gap-2 sm:grid-cols-2">
                <input value={anahtar} onChange={(e) => setAnahtar(e.target.value)} placeholder="glp_ ile başlayan bağlantı anahtarı"
                  className="rounded-lg border bg-transparent px-3 py-2 font-mono text-[12.5px] outline-none dark:border-white/15" />
                <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="WP kök adresi (boşsa bu site)"
                  className="rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
              </div>
              <Button className="mt-3 gap-2 bg-brand-500 hover:bg-brand-600" disabled={baglan.isPending || !anahtar.trim()} onClick={() => baglan.mutate()}>
                {baglan.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Bağlan ve Doğrula
              </Button>
            </Card>
          ) : (
            <Card className="p-4">
              <p className="mb-3 max-w-2xl text-[12px] text-muted-foreground">
                WP yönetim panelinde <b>Kullanıcılar → Profil → Uygulama Şifreleri</b> bölümünden "Geolop" adına şifre üret
                (editör ve üstü yetkili kullanıcıda; normal giriş şifresi ÇALIŞMAZ). Giriş adresin gizliyse sorun değil — Geolop panele
                değil sitenin REST kapısına (<code>/wp-json</code>) bağlanır. "Şifre hatalı" hatası alıyorsan büyük ihtimal
                sunucun Authorization başlığını yutuyor — o durumda eklenti yolunu kullan (soldaki seçenek), kesin çözümdür.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                <input value={kullanici} onChange={(e) => setKullanici(e.target.value)} placeholder="WP kullanıcı adı"
                  className="rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
                <input value={sifre} onChange={(e) => setSifre(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" type="password"
                  className="rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
                <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="WP kök adresi (boşsa bu site)"
                  className="rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
              </div>
              <Button className="mt-3 gap-2 bg-brand-500 hover:bg-brand-600" disabled={baglan.isPending || !kullanici.trim() || !sifre.trim()} onClick={() => baglan.mutate()}>
                {baglan.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Bağlan ve Doğrula
              </Button>
            </Card>
          )}
        </>
      )}

      {(durum.data?.uygulamalar?.length ?? 0) > 0 && (
        <Card className="p-4">
          <div className="mb-2 text-[12.5px] font-semibold">Son uygulamalar</div>
          <div className="space-y-1.5">
            {durum.data!.uygulamalar.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-[12px]">
                {u.tur === "geri-alma" ? <Undo2 className="h-3.5 w-3.5 text-amber-500" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                <span className="font-medium">{TUR_TR[u.tur]}</span>
                <span className="truncate text-muted-foreground">{u.detay?.yeniTitle ?? u.detay?.eskiTitle ?? ""}</span>
                {u.durum === "geri-alindi" && <span className="rounded-full bg-amber-500/15 px-1.5 text-[10px] font-bold text-amber-600">GERİ ALINDI</span>}
                <span className="ml-auto shrink-0 text-muted-foreground">{new Date(u.createdAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                {u.detay?.wpLink && <a href={u.detay.wpLink} target="_blank" rel="noopener noreferrer" className="text-brand-500"><ExternalLink className="h-3.5 w-3.5" /></a>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
